// IndexedDB工具类，用于存储和管理聊天记录
class ChatIndexedDB {
  constructor() {
    this.dbName = 'ChatDB';
    this.storeName = 'chatRecords';
    this.db = null;
  }

  // 初始化数据库
  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 2);

      request.onerror = (event) => {
        console.error('IndexedDB打开失败:', event.target.error);
        reject(event.target.error);
      };

      request.onsuccess = (event) => {
        this.db = event.target.result;
        resolve(this.db);
      };

      // 如果数据库不存在或需要升级
      request.onupgradeneeded = (event) => {
        this.db = event.target.result;

        // 创建存储对象
        if (!this.db.objectStoreNames.contains(this.storeName)) {
          const store = this.db.createObjectStore(this.storeName, {
            keyPath: 'id',
            autoIncrement: true
          });

          // 创建索引以加速查询
          store.createIndex('userId', 'userId', { unique: false });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('record_id', 'record_id', { unique: false });
        } else {
          // 对于已存在的存储，确保有record_id索引
          const store = event.target.transaction.objectStore(this.storeName);
          if (!store.indexNames.contains('record_id')) {
            store.createIndex('record_id', 'record_id', { unique: false });
          }
        }
      };
    });
  }

  // 存储聊天记录（新增记录）
  async storeChatRecord(record) {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);

      // 确保记录包含timestamp
      const recordWithTimestamp = {
        ...record,
        timestamp: record.timestamp || Date.now()
      };

      const request = store.add(recordWithTimestamp);

      request.onerror = (event) => {
        console.error('存储聊天记录失败:', event.target.error);
        reject(event.target.error);
      };

      request.onsuccess = (event) => {
        resolve(event.target.result); // 返回生成的ID
      };
    });
  }

  // 更新聊天记录（根据record_id查找并更新现有记录）
  async updateChatRecordByRecordId(oldRecordId, updatedData) {
    console.log('更新聊天记录:', oldRecordId, updatedData);
    if (!this.db) {
      await this.init();
    }

    //如果oldRecordId为空 直接返回null
    if(!oldRecordId){
      return null
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);

      // 首先尝试通过record_id索引查找记录
      const findByRecordId = () => {
        // 确保record_id索引存在
        if (store.indexNames.contains('record_id')) {
          const index = store.index('record_id');
          const request = index.get(oldRecordId);

          request.onerror = (event) => {
            console.error('通过record_id查找聊天记录失败:', event.target.error);
            // 如果通过索引查找失败，尝试遍历所有记录
            findByFullScan();
          };

          request.onsuccess = (event) => {
            if (event.target.result) {
              updateRecord(event.target.result);
            } else {
              console.warn(`通过索引未找到record_id为${oldRecordId}的聊天记录，尝试遍历查找`);
              // 如果通过索引查找不到，尝试遍历所有记录
              findByFullScan();
            }
          };
        } else {
          findByFullScan();
        }
      };

      // 如果通过索引查找失败，遍历所有记录查找
      const findByFullScan = () => {
        const request = store.openCursor();
        let foundRecord = null;

        request.onerror = (event) => {
          console.error('遍历查找聊天记录失败:', event.target.error);
          resolve(null);
        };

        request.onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
           // 扩展查找逻辑：不仅查找主record_id，也查找style数组中的record_id
            if ( cursor.value.record_id ===oldRecordId ) {
              foundRecord = cursor.value;
              updateRecord(foundRecord);
            }else if (cursor.value.style && Array.isArray(cursor.value.style)) {
              // 检查style数组中是否有匹配的record_id
              const styleItem = cursor.value.style.find(item => item.record_id === oldRecordId);
              if (styleItem) {
                foundRecord = cursor.value;
                updateRecord(foundRecord);
              } else {
                cursor.continue();
              }
            }
          else {
              cursor.continue();
            }
          } else {
            // 遍历结束，未找到记录
            console.warn(`遍历后仍未找到record_id为${oldRecordId}的聊天记录`);
            resolve(null);
          }
        };
      };

      // 更新找到的记录
      const updateRecord = (existingRecord) => {

        // 创建更新记录，合并所有传入的更新字段
        const updatedRecord = {
          ...existingRecord,
          ...updatedData
        };

        // 确保不会覆盖timestamp字段
        if (!updatedData.timestamp) {
          updatedRecord.timestamp = existingRecord.timestamp;
        }

        const updateRequest = store.put(updatedRecord);

        updateRequest.onerror = (event) => {
          console.error('更新聊天记录失败:', event.target.error);
          reject(event.target.error);
        };

        updateRequest.onsuccess = (event) => {
          resolve(event.target.result);
        };
      };

      // 开始查找过程
      findByRecordId();
    });
  }

  // 获取用户的所有聊天记录
  async getUserChatRecords(userId) {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('userId');
      const request = index.getAll(userId);

      request.onerror = (event) => {
        console.error('获取聊天记录失败:', event.target.error);
        reject(event.target.error);
      };

      request.onsuccess = (event) => {
        resolve(event.target.result);
      };
    });
  }

  // 清除用户的所有聊天记录
  async clearUserChatRecords(userId) {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('userId');
      const request = index.openCursor(userId);

      const deletions = [];

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          deletions.push(store.delete(cursor.primaryKey));
          cursor.continue();
        } else {
          // 所有记录都已处理
          Promise.all(deletions).then(() => resolve()).catch(reject);
        }
      };

      request.onerror = (event) => {
        console.error('清除聊天记录失败:', event.target.error);
        reject(event.target.error);
      };
    });
  }
}

// 导出单例实例
export default new ChatIndexedDB();
